import {
  DeleteOutlined,
  DownloadOutlined,
  EyeOutlined,
  FileExcelOutlined,
  FileImageOutlined,
  FilePdfOutlined,
  FileTextOutlined,
  FileWordOutlined,
} from '@ant-design/icons';
import { App, Button, List, Space, Typography } from 'antd';
import { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

import { ResourceDocument } from '@/models';

const { Text } = Typography;

type ResourceDocumentListProps = {
  documents: ResourceDocument[];
  isLoading: boolean;
  onDelete: (id: string) => void;
};

const formatFileSize = (bytes: number): string => {
  if (bytes === 0) return '0 Bytes';
  const k = 1024;
  const sizes = ['Bytes', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return `${Math.round((bytes / Math.pow(k, i)) * 100) / 100} ${sizes[i]}`;
};

const getFileIcon = (mimeType: string): ReactNode => {
  const iconStyle = { fontSize: 20, color: '#1890ff' };

  if (mimeType === 'application/pdf') {
    return <FilePdfOutlined style={{ ...iconStyle, color: '#ff4d4f' }} />;
  }

  if (
    mimeType === 'application/vnd.ms-excel' ||
    mimeType === 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' ||
    mimeType === 'text/csv'
  ) {
    return <FileExcelOutlined style={{ ...iconStyle, color: '#52c41a' }} />;
  }

  if (mimeType.startsWith('image/')) {
    return <FileImageOutlined style={{ ...iconStyle, color: '#1890ff' }} />;
  }

  if (
    mimeType === 'application/msword' ||
    mimeType === 'application/vnd.openxmlformats-officedocument.wordprocessingml.document'
  ) {
    return <FileWordOutlined style={{ ...iconStyle, color: '#1890ff' }} />;
  }

  return <FileTextOutlined style={iconStyle} />;
};

export const ResourceDocumentList = ({
  documents,
  isLoading,
  onDelete,
}: ResourceDocumentListProps) => {
  const { modal } = App.useApp();
  const { t } = useTranslation();

  return (
    <List
      size='small'
      itemLayout='horizontal'
      dataSource={documents}
      loading={isLoading}
      renderItem={(item) => (
        <List.Item
          actions={[
            <Button
              key='view'
              type='link'
              icon={<EyeOutlined />}
              href={item.viewUrl}
              target='_blank'
              rel='noreferrer'
            ></Button>,
            <Button
              key='download'
              type='link'
              icon={<DownloadOutlined />}
              href={item.downloadUrl}
              target='_blank'
              rel='noreferrer'
            ></Button>,
            <Button
              danger
              key='delete'
              type='link'
              icon={<DeleteOutlined />}
              onClick={() =>
                modal.confirm({
                  title: t('forms:deleteDocument'),
                  content: t('forms:deleteDocumentDescription'),
                  onOk: () => onDelete(item.id),
                  okButtonProps: { danger: true },
                  okText: t('forms:delete'),
                })
              }
            ></Button>,
          ]}
        >
          <List.Item.Meta
            avatar={getFileIcon(item.mimeType)}
            title={
              <Space size='small' separator='-'>
                <Text strong>{item.fileName}</Text>
                <Text type='secondary'>{formatFileSize(item.size)}</Text>
              </Space>
            }
          />
        </List.Item>
      )}
    />
  );
};
